/**
 * RecommendationEngine – produces context-aware "next best action"
 * suggestions based on the current screen, data, workflow state, and the
 * user's role.
 *
 * Pure function. Consumers (e.g. RecommendationsPanel + useRecommendations)
 * are responsible for executing the action.
 */

import type { UIContext } from './UIContextExtractor';
import { findKpi, kpiValue } from './UIContextExtractor';

export type RecommendationPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export type RecommendationActionType =
  | 'NAVIGATE'
  | 'CREATE'
  | 'WORKFLOW'
  | 'FILTER'
  | 'OPEN'
  | 'INFO';

export interface Recommendation {
  id: string;
  label: string;
  description?: string;
  priority: RecommendationPriority;
  type: RecommendationActionType;
  /** Lucide icon name; resolved by UI. */
  icon?: 'AlertTriangle' | 'Clock' | 'FileText' | 'Plus' | 'Workflow' | 'Filter' | 'TrendingUp' | 'Phone' | 'CheckCircle' | 'ArrowRight';
  /** Route for NAVIGATE. */
  route?: string;
  /** CSS selector for OPEN/FILTER (element to scroll/click). */
  target?: string;
  /** Free-form payload for CREATE actions (entityType, formKey, etc.). */
  payload?: Record<string, unknown>;
}

const PRIORITY_RANK: Record<RecommendationPriority, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

export function sortByPriority(list: Recommendation[]): Recommendation[] {
  return [...list].sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);
}

// ──────────────────────────────────────────────────────────────────────
// Role classification
// ──────────────────────────────────────────────────────────────────────

type SimpleRole = 'CC' | 'CCM' | 'ADMIN' | 'OTHER';

function classifyRole(role: string | undefined): SimpleRole {
  if (!role) return 'OTHER';
  const r = role.toLowerCase();
  if (r.includes('case manager') || r.includes('ccm')) return 'CCM';
  if (r.includes('care coordinator') || r.includes('cc ') || r === 'cc') return 'CC';
  if (r.includes('admin') || r.includes('superadmin')) return 'ADMIN';
  return 'OTHER';
}

// ──────────────────────────────────────────────────────────────────────
// Generation
// ──────────────────────────────────────────────────────────────────────

export function generateRecommendations(ctx: UIContext): Recommendation[] {
  const role = classifyRole(ctx.role);
  const out: Recommendation[] = [];

  // ─── Cross-cutting: SLA breaches always trump everything ────────────
  if (ctx.workflow.slaBreached > 0) {
    out.push({
      id: 'sla-escalation',
      label: `Resolve ${ctx.workflow.slaBreached} SLA-breached task${ctx.workflow.slaBreached === 1 ? '' : 's'}`,
      description: 'These tasks have passed their service-level deadline and need immediate attention.',
      priority: 'CRITICAL',
      type: 'NAVIGATE',
      icon: 'AlertTriangle',
      route: ctx.programId ? `/programs/${ctx.programId}/workspace/tasks?slaBreached=true` : '/tasks',
    });
  }

  // ─── Cross-cutting: alerts visible on the page ──────────────────────
  if (ctx.alerts.length > 0) {
    out.push({
      id: 'page-alert',
      label: 'Address page alerts',
      description: ctx.alerts[0],
      priority: 'HIGH',
      type: 'INFO',
      icon: 'AlertTriangle',
    });
  }

  switch (ctx.pageType) {
    // ─── Dashboard ────────────────────────────────────────────────────
    case 'Dashboard': {
      const pendingApprovals = kpiValue(ctx, 'pending') || kpiValue(ctx, 'approval');
      if (pendingApprovals > 0 && (role === 'CCM' || role === 'ADMIN')) {
        out.push({
          id: 'review-pending',
          label: `Review ${pendingApprovals} pending approval${pendingApprovals === 1 ? '' : 's'}`,
          priority: 'HIGH',
          type: 'NAVIGATE',
          icon: 'CheckCircle',
          route: ctx.programId ? `/programs/${ctx.programId}/workspace/tasks` : '/tasks',
        });
      }

      const newPatients = kpiValue(ctx, 'new');
      if (newPatients > 0 && role === 'CC') {
        out.push({
          id: 'follow-up-new',
          label: `Follow up on ${newPatients} new patient${newPatients === 1 ? '' : 's'}`,
          priority: 'HIGH',
          type: 'NAVIGATE',
          icon: 'Phone',
          route: ctx.programId ? `/programs/${ctx.programId}/workspace/patients?filter=new` : '/patients',
        });
      }

      const utilization = kpiValue(ctx, 'utiliz');
      if (utilization > 0 && utilization < 30) {
        out.push({
          id: 'low-utilization',
          label: 'Investigate low utilization',
          description: `Current utilization is ${utilization}%. Review patient engagement.`,
          priority: 'MEDIUM',
          type: 'NAVIGATE',
          icon: 'TrendingUp',
          route: ctx.programId ? `/programs/${ctx.programId}/reports` : '/reports',
        });
      }
      break;
    }

    // ─── Patient List ─────────────────────────────────────────────────
    case 'PatientList': {
      const total = ctx.tableSummary?.totalRecords ?? 0;
      if (total === 0) {
        out.push({
          id: 'enroll-first-patient',
          label: 'Enroll the first patient',
          priority: 'HIGH',
          type: 'CREATE',
          icon: 'Plus',
          payload: { entityType: 'PATIENT' },
        });
      } else if (!ctx.tableSummary?.hasFilters && total > 50) {
        out.push({
          id: 'apply-filter',
          label: 'Apply filters to narrow the list',
          description: `${total} records visible — filtering helps you focus.`,
          priority: 'LOW',
          type: 'FILTER',
          icon: 'Filter',
          target: '[data-demo="filter-panel"]',
        });
      }

      if (role === 'CC') {
        out.push({
          id: 'enroll-new-patient',
          label: 'Enroll a new patient',
          priority: 'MEDIUM',
          type: 'CREATE',
          icon: 'Plus',
          payload: { entityType: 'PATIENT' },
        });
      }
      break;
    }

    // ─── Patient 360 ──────────────────────────────────────────────────
    case 'Patient360': {
      const cases = ctx.entity?.cases ?? 0;

      if (cases === 0) {
        out.push({
          id: 'create-first-case',
          label: 'Create the first assistance case',
          description: 'This patient has no cases yet — start their assistance journey.',
          priority: 'HIGH',
          type: 'CREATE',
          icon: 'Plus',
          payload: { entityType: 'CASE', patientId: ctx.patientId },
          target: '[data-demo="create-case-btn"]',
        });
      }

      if (ctx.workflow.pendingTasks > 0) {
        out.push({
          id: 'review-workflow-task',
          label: `Review ${ctx.workflow.pendingTasks} workflow task${ctx.workflow.pendingTasks === 1 ? '' : 's'}`,
          priority: 'HIGH',
          type: 'WORKFLOW',
          icon: 'Workflow',
        });
      }

      if (role === 'CC') {
        out.push({
          id: 'log-follow-up',
          label: 'Log a follow-up call',
          priority: 'MEDIUM',
          type: 'OPEN',
          icon: 'Phone',
          target: '[data-demo="call-log-section"]',
        });
      }

      // Missing-document hint when section name suggests documents but no rows.
      const hasDocSection = ctx.visibleSections.some((s) => /document/i.test(s));
      const hasDocAttr = document.querySelector('[data-document-count]')?.getAttribute('data-document-count');
      if (hasDocSection && hasDocAttr === '0') {
        out.push({
          id: 'upload-docs',
          label: 'Upload missing documents',
          priority: 'MEDIUM',
          type: 'OPEN',
          icon: 'FileText',
          target: '[data-section-name="documents"]',
        });
      }
      break;
    }

    // ─── Case Detail ──────────────────────────────────────────────────
    case 'CaseDetail': {
      if (ctx.workflow.pendingTasks > 0 && role !== 'CC') {
        out.push({
          id: 'take-workflow-action',
          label: 'Take a workflow action',
          description: `Current step: ${ctx.workflow.currentStep ?? 'pending'}.`,
          priority: 'HIGH',
          type: 'WORKFLOW',
          icon: 'Workflow',
          target: '[data-demo="approve-reject-btns"]',
        });
      }

      const utilized = ctx.entity?.utilizedAmount;
      const approved = ctx.entity?.approvedAmount;
      if (utilized && approved) {
        out.push({
          id: 'review-utilization',
          label: 'Review utilization vs approved limit',
          priority: 'MEDIUM',
          type: 'OPEN',
          icon: 'TrendingUp',
          target: '[data-section-name="utilization"], [data-demo="ledger"]',
        });
      }
      break;
    }

    // ─── Workflow ─────────────────────────────────────────────────────
    case 'Workflow': {
      if (ctx.pendingTasks > 0) {
        out.push({
          id: 'complete-pending-tasks',
          label: `Complete ${ctx.pendingTasks} pending task${ctx.pendingTasks === 1 ? '' : 's'}`,
          priority: 'HIGH',
          type: 'WORKFLOW',
          icon: 'CheckCircle',
        });
      } else {
        out.push({
          id: 'no-pending',
          label: 'No pending tasks',
          description: 'You are all caught up.',
          priority: 'LOW',
          type: 'INFO',
          icon: 'CheckCircle',
        });
      }
      break;
    }

    // ─── Assistance Cases ─────────────────────────────────────────────
    case 'AssistanceList': {
      const total = ctx.tableSummary?.totalRecords ?? 0;
      if (total > 0 && !ctx.tableSummary?.hasFilters) {
        out.push({
          id: 'filter-cases',
          label: 'Filter by status or plan',
          priority: 'LOW',
          type: 'FILTER',
          icon: 'Filter',
          target: '[data-demo="filter-panel"]',
        });
      }
      break;
    }

    // ─── Reports ──────────────────────────────────────────────────────
    case 'Reports': {
      out.push({
        id: 'open-utilization-report',
        label: 'Open the utilization trend report',
        priority: 'MEDIUM',
        type: 'OPEN',
        icon: 'TrendingUp',
      });
      break;
    }

    default:
      break;
  }

  // ─── Role-based filtering ──────────────────────────────────────────
  // CC should not see admin-only escalation; CCM should not see basic
  // creation suggestions duplicated. We do a light pass to drop obviously
  // wrong-role recommendations.
  const filtered = out.filter((rec) => {
    if (role === 'CC' && (rec.id === 'sla-escalation' && ctx.pageType !== 'Workflow')) return false;
    if (role === 'CCM' && rec.id === 'log-follow-up') return false;
    return true;
  });

  return sortByPriority(filtered);
}

/** Pretty label for priority badge. */
export function priorityLabel(p: RecommendationPriority): string {
  return p.charAt(0) + p.slice(1).toLowerCase();
}

// Keep `findKpi` re-exported so consumers who want raw KPI lookup can
// import from here without pulling in two modules.
export { findKpi };
